import { useState, useEffect, useRef } from 'react';

import useAuthStore from '@entities/auth/model/useAuthStore';
import {
  useGetEmployeesByTeamQuery,
  useGetEmployeeByNameQuery,
} from '@features/employee/hooks/index';
import useGetTeamQuery from '@features/team/hooks';

/**
 * useMobileRecipientSelector
 * 모바일 참조자 선택 컴포넌트의 비즈니스 로직 훅
 *
 * @param {boolean} isOpen - 모달 열림 여부
 * @param {function} onClose - 모달 닫기 콜백
 * @param {function} onSelect - 선택 완료 콜백
 * @param {array} selectedRecipients - 기존 선택된 참조자 목록
 */
export default function useMobileRecipientSelector({
  isOpen,
  onClose,
  onSelect,
  selectedRecipients = [],
}) {
  // 상태 관리
  const [step, setStep] = useState(1); // 1: 직원 선택, 2: 선택 확인
  const [selectedTeamCode, setSelectedTeamCode] = useState(null);
  const [searchName, setSearchName] = useState('');
  const [searchTrigger, setSearchTrigger] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [expandedTeams, setExpandedTeams] = useState(new Set());

  // 초기화 여부 추적 (무한 루프 방지)
  const isInitializedRef = useRef(false);
  const prevIsOpenRef = useRef(false);

  const MAX_SELECTED = 20;

  // 회사 코드
  const companyCode = useAuthStore(function (state) {
    return state.companyCode;
  });

  // 팀 목록 조회
  const teamQuery = useGetTeamQuery(companyCode, {
    enabled: isOpen && !!companyCode,
  });
  const teamList = teamQuery.data?.resultData?.teamList || [];

  // 팀별 직원 목록 조회
  const employeeQuery = useGetEmployeesByTeamQuery(selectedTeamCode, {
    enabled: isOpen && !!selectedTeamCode,
  });
  const employees = employeeQuery.data?.resultData?.employeeList || [];

  // 직원 이름 검색
  const employeeNameQuery = useGetEmployeeByNameQuery(searchTrigger, {
    enabled: isOpen && searchTrigger.length >= 1,
  });
  const searchResults = employeeNameQuery.data?.resultData?.employeeList || [];

  // 표시할 직원 목록
  const showSearchResults = searchTrigger.length >= 1;
  const displayEmployees = showSearchResults ? searchResults : employees;

  // 모달 열릴 때 초기화 (무한 루프 방지)
  useEffect(
    function () {
      // 모달이 열릴 때만 초기화 (false → true 전환 시)
      if (isOpen && !prevIsOpenRef.current) {
        const initialEmployees =
          selectedRecipients && selectedRecipients.length > 0
            ? selectedRecipients.map(function (r) {
                return {
                  enumber: r.enumber || 0,
                  name: r.name || '',
                  position: r.position || '',
                  team: r.team || '',
                };
              })
            : [];
        setSelectedEmployees(initialEmployees);
        setStep(1);
        setSelectedTeamCode(null);
        setSearchName('');
        setSearchTrigger('');
        isInitializedRef.current = true;
      }

      // 모달 닫힐 때 초기화 플래그 리셋
      if (!isOpen) {
        isInitializedRef.current = false;
      }

      prevIsOpenRef.current = isOpen;
    },
    [isOpen, selectedRecipients],
  );

  // 팀 목록 로드 시 루트 팀 펼침
  useEffect(
    function () {
      if (isOpen && teamList.length > 0) {
        const rootTeams = teamList
          .filter(function (t) {
            return !t.departmentCode || t.departmentCode === 0;
          })
          .map(function (t) {
            return t.teamCode;
          });
        setExpandedTeams(function (prev) {
          if (prev.size === 0) {
            return new Set(rootTeams);
          }
          return prev;
        });
      }
    },
    [isOpen, teamList.length],
  );

  // 팀 트리 구성
  function buildTree() {
    const byCode = new Map();
    const childrenMap = new Map();
    const roots = [];

    teamList.forEach(function (item) {
      const code = Number(item?.teamCode);
      if (!code) return;
      byCode.set(code, item);
    });

    byCode.forEach(function (item, teamCode) {
      const parentCode = Number(item?.departmentCode) || 0;
      const hasParent = parentCode !== 0 && byCode.has(parentCode);

      if (!hasParent) {
        roots.push(teamCode);
      } else {
        if (!childrenMap.has(parentCode)) {
          childrenMap.set(parentCode, []);
        }
        childrenMap.get(parentCode).push(teamCode);
      }
    });

    childrenMap.forEach(function (childCodes) {
      childCodes.sort(function (a, b) {
        const aSort = byCode.get(a)?.sort ?? 0;
        const bSort = byCode.get(b)?.sort ?? 0;
        return aSort - bSort;
      });
    });

    roots.sort(function (a, b) {
      const aSort = byCode.get(a)?.sort ?? 0;
      const bSort = byCode.get(b)?.sort ?? 0;
      return aSort - bSort;
    });

    return { roots, childrenMap, byCode };
  }

  const tree = buildTree();

  // 팀 토글
  function toggleTeam(teamCode) {
    setExpandedTeams(function (prev) {
      const next = new Set(prev);
      if (next.has(teamCode)) {
        next.delete(teamCode);
      } else {
        next.add(teamCode);
      }
      return next;
    });
  }

  // 팀 선택
  function handleTeamSelect(teamCode) {
    setSelectedTeamCode(teamCode);
    setSearchName('');
    setSearchTrigger('');
  }

  // 직원 선택/해제
  function handleEmployeeToggle(employee) {
    const isSelected = selectedEmployees.some(function (emp) {
      return emp.enumber === employee.enumber;
    });

    if (isSelected) {
      setSelectedEmployees(function (prev) {
        return prev.filter(function (emp) {
          return emp.enumber !== employee.enumber;
        });
      });
    } else {
      if (selectedEmployees.length >= MAX_SELECTED) {
        return;
      }
      setSelectedEmployees(function (prev) {
        return [
          ...prev,
          {
            enumber: employee.enumber,
            name: employee.name,
            position: employee.position || '',
            team: employee.team || '',
          },
        ];
      });
    }
  }

  // 직원 제거
  function handleRemoveEmployee(index) {
    setSelectedEmployees(function (prev) {
      return prev.filter(function (_, i) {
        return i !== index;
      });
    });
  }

  // 검색
  function handleSearch() {
    setSearchTrigger(searchName);
    setSelectedTeamCode(null);
  }

  // 검색어 변경
  function handleSearchNameChange(value) {
    setSearchName(value);
  }

  // 다음 버튼
  function handleNext() {
    setStep(2);
  }

  // 뒤로가기
  function handleBack() {
    if (step === 2) {
      setStep(1);
    } else {
      onClose();
    }
  }

  // 선택 완료
  function handleComplete() {
    if (onSelect) {
      onSelect(selectedEmployees);
    }
    onClose();
  }

  // 직원이 선택되었는지 확인
  function isEmployeeSelected(employee) {
    return selectedEmployees.some(function (emp) {
      return emp.enumber === employee.enumber;
    });
  }

  // 전체 선택 여부 확인
  function isAllSelected() {
    if (displayEmployees.length === 0) {
      return false;
    }
    return displayEmployees.every(function (emp) {
      return isEmployeeSelected(emp);
    });
  }

  // 전체 선택/해제
  function handleSelectAll() {
    const allSelected = isAllSelected();

    if (allSelected) {
      // 전체 해제: 현재 표시된 직원들만 선택 해제
      setSelectedEmployees(function (prev) {
        return prev.filter(function (emp) {
          return !displayEmployees.some(function (dispEmp) {
            return dispEmp.enumber === emp.enumber;
          });
        });
      });
    } else {
      // 전체 선택: 현재 표시된 직원들 중 미선택된 것만 추가
      const notSelected = displayEmployees.filter(function (emp) {
        return !isEmployeeSelected(emp);
      });

      // MAX_SELECTED 제한 확인
      const canAdd = MAX_SELECTED - selectedEmployees.length;
      const toAdd = notSelected.slice(0, canAdd);

      if (toAdd.length > 0) {
        setSelectedEmployees(function (prev) {
          return [
            ...prev,
            ...toAdd.map(function (emp) {
              return {
                enumber: emp.enumber,
                name: emp.name,
                position: emp.position || '',
                team: emp.team || '',
              };
            }),
          ];
        });
      }
    }
  }

  return {
    // 상태
    step,
    searchName,
    selectedEmployees,
    expandedTeams,
    selectedTeamCode,
    showSearchResults,
    displayEmployees,
    teamList,
    tree,

    // 핸들러
    toggleTeam,
    handleTeamSelect,
    handleEmployeeToggle,
    handleRemoveEmployee,
    handleSearch,
    handleSearchNameChange,
    handleNext,
    handleBack,
    handleComplete,
    handleSelectAll,

    // 유틸리티
    isEmployeeSelected,
    isAllSelected,
  };
}
